import { GRADES, gradeLabel, isGrade } from "@vavr/core";
// Rooms (classes, groups): create, rename, and assign teachers. A teacher sees only their rooms.
import { useState, type ReactElement } from "react";
import { Banner, Select, Button, Checkbox, Dialog, DialogClose, Table, TextField } from "@vavr/ui";
import { api } from "../shared/index.ts";
import { useConsole, useGet, useAction } from "./data.tsx";
import { LoadError, Loading, Toolbar } from "./common.tsx";

interface Room { id: string; name: string; teacher_ids: string[]; grade?: string | null }
interface Member { id: string; account_id: string; name: string; email: string; role: string; room_ids: string[] }

export function Rooms() {
  const { base, can } = useConsole();
  const rooms = useGet<{ rooms: Room[] }>(`${base}/rooms`);
  const children = useGet<{ children: { room_id: string | null; status: string }[] }>(`${base}/children`, 30_000);
  const members = useGet<{ members: Member[] }>(can("staff.manage") ? `${base}/members` : null, 30_000);
  if (rooms.loading) return <Loading />;
  if (rooms.error) return <LoadError error={rooms.error} retry={rooms.reload} />;
  const teachers = (members.data?.members ?? []).filter((m) => m.role === "teacher");
  const nameOf = (id: string) => members.data?.members.find((m) => m.account_id === id)?.name ?? "A teacher";
  // A teacher's membership scope is what limits them, so it is the source of truth; the room's
  // own list can lag behind it (an invitation sets the scope only).
  const teachersOf = (r: Room) => [...new Set([...r.teacher_ids, ...teachers.filter((t) => t.room_ids.includes(r.id)).map((t) => t.account_id)])];
  const count = (id: string) => (children.data?.children ?? []).filter((c) => c.room_id === id && c.status === "active").length;
  return (
    <div className="pmu-stack">
      <Toolbar actions={can("roster.write") && <RoomDialog trigger={<Button icon="plus">Add a room</Button>} />} />
      <Table
        caption="Rooms" hideCaption rowKey={(r) => r.id} rows={rooms.data?.rooms ?? []}
        empty="No rooms yet. Add one, or they are created when you import a roster."
        columns={[
          { key: "name", header: "Room", rowHeader: true },
          { key: "grade", header: "Grade", render: (r) => (isGrade(r.grade) ? gradeLabel(r.grade) : "Not set") },
          { key: "kids", header: "Children", align: "end", render: (r) => count(r.id) },
          { key: "t", header: "Teachers", render: (r) => (teachersOf(r).length ? teachersOf(r).map(nameOf).join(", ") : <span className="pmu-muted">None</span>) },
          {
            key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (r) => (
              <span className="pmu-row pmu-c-row-actions">
                {can("roster.write") && <RoomDialog room={r} trigger={<Button size="sm" variant="ghost">Edit</Button>} />}
                {can("staff.manage") && <TeachersDialog room={r} current={teachersOf(r)} teachers={teachers} trigger={<Button size="sm" variant="ghost">Teachers</Button>} />}
              </span>
            ),
          },
        ]}
      />
      {can("staff.manage") && teachers.length === 0 && <Banner tone="info" title="No teachers yet">Invite teachers under Staff, then assign them here.</Banner>}
    </div>
  );
}

function RoomDialog({ room, trigger }: { room?: Room; trigger: ReactElement }) {
  const { base } = useConsole();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(room?.name ?? "");
  const [grade, setGrade] = useState(room?.grade ?? "");
  const act = useAction();
  const save = async () => {
    // The room upsert replaces the teacher list, so a rename sends the current one.
    const ok = await act.run(() => api("POST", `${base}/rooms`, { id: room?.id, name, teacher_ids: room?.teacher_ids ?? [], grade: grade || null }), { success: room ? "Room saved." : "Room added.", invalidate: [`${base}/rooms`] });
    if (ok) { setOpen(false); if (!room) setName(""); }
  };
  return (
    <Dialog title={room ? `Edit ${room.name}` : "Add a room"} open={open} onOpenChange={setOpen} trigger={trigger}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!name.trim()} onClick={save}>Save</Button></>}>
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) void save(); }} className="pmu-stack">
        {act.error && <Banner tone="danger" title={act.error} />}
        <TextField label="Room name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Butterflies, Grade 2, After-school" />
        <Select label="Grade" hint="The school's rules (children walking in, the bus stop) go by grade. A child in a mixed room can have their own." value={grade || "none"} onValueChange={(v) => setGrade(v === "none" || !v ? "" : v)}
          options={[{ value: "none", label: "Not set" }, ...GRADES.map((g) => ({ value: g, label: gradeLabel(g) }))]} />
      </form>
    </Dialog>
  );
}

function TeachersDialog({ room, current, teachers, trigger }: { room: Room; current: string[]; teachers: Member[]; trigger: ReactElement }) {
  const { base } = useConsole();
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState<string[]>(current);
  const act = useAction();
  return (
    <Dialog title={`Teachers for ${room.name}`} description="Teachers see only the rooms they are assigned to." open={open} onOpenChange={(o) => { setOpen(o); if (o) setIds(current); }} trigger={trigger}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} onClick={async () => {
        const ok = await act.run(() => api("PUT", `${base}/rooms/${room.id}/teachers`, { teacher_ids: ids }), { success: "Teachers saved.", invalidate: [`${base}/rooms`, `${base}/members`] });
        if (ok) setOpen(false);
      }}>Save</Button></>}>
      <div className="pmu-stack">
        {act.error && <Banner tone="danger" title={act.error} />}
        {teachers.length === 0 && <p className="pmu-muted">No teachers at this site yet. Invite one under Staff.</p>}
        {teachers.map((t) => (
          <Checkbox key={t.account_id} label={t.name} description={t.email} checked={ids.includes(t.account_id)}
            onCheckedChange={(c) => setIds((x) => (c ? [...x, t.account_id] : x.filter((y) => y !== t.account_id)))} />
        ))}
      </div>
    </Dialog>
  );
}
